import type { DocumentRegistryEntry, VersionFamilyMember } from '@kennisbank/shared';

/**
 * Display helpers for document-version families (admin Versions view +
 * the badges on /documents). Kept pure so they are unit-testable.
 */

// OneNebula HQ timezone — storage is UTC, rendering applies the tz.
const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Africa/Johannesburg',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export function formatEffectiveDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATE_FMT.format(d);
}

export const EFFECTIVE_DATE_SOURCE_LABEL: Record<string, string> = {
  filename: 'date in the filename',
  file_modified: 'file last modified',
  uploaded: 'upload time',
};

export function similarityLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${Math.round(value * 100)}%`;
}

export type VersionBadge =
  | { kind: 'superseded'; label: string; tooltip: string }
  | { kind: 'current'; label: string; tooltip: string };

/**
 * Badge for a row on /documents. `current` only appears on rows that
 * actually have older copies (detection writes `current` only then);
 * `supersededBy` may be null when the newer file is not visible to the
 * caller, in which case the tooltip stays generic.
 */
export function versionBadge(
  doc: Pick<DocumentRegistryEntry, 'versionStatus' | 'supersededBy'>,
): VersionBadge | null {
  if (doc.versionStatus === 'superseded') {
    return {
      kind: 'superseded',
      label: 'Superseded',
      tooltip: doc.supersededBy
        ? `A newer version exists: ${doc.supersededBy}. The assistant prefers the newer copy.`
        : 'A newer version of this document exists. The assistant prefers the newer copy.',
    };
  }
  if (doc.versionStatus === 'current') {
    return {
      kind: 'current',
      label: 'Current version',
      tooltip: 'Older copies of this document exist; this is the one the assistant prefers.',
    };
  }
  return null;
}

export function statusLabel(
  m: Pick<VersionFamilyMember, 'versionStatus' | 'versionConfidence'>,
): string {
  if (m.versionConfidence === 'review') return 'Needs review';
  if (m.versionStatus === 'current') return 'Current';
  if (m.versionStatus === 'superseded') return 'Superseded';
  return 'Separate';
}
